import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import axios from "axios";
import { Toast } from "primereact/toast";
import {
  FaCloudUploadAlt,
  FaImages,
  FaTrash,
  FaTimes,
  FaSyncAlt,
  FaExpand,
  FaChevronLeft,
  FaChevronRight,
  FaExternalLinkAlt,
  FaCopy,
  FaDownload,
  FaTh,
  FaList,
  FaCheckCircle,
  FaExclamationCircle,
  FaPlus,
  FaMobileAlt,
  FaClock,
  FaRulerCombined,
} from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

/* ================= styles ================= */

const CSS = `
@keyframes adFadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@keyframes adFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes adZoom { from { opacity: 0; transform: scale(.96); } to { opacity: 1; transform: none; } }
.ad-in { animation: adFadeUp .45s cubic-bezier(.2,.7,.2,1) both; }
.ad-fade { animation: adFade .2s ease both; }
.ad-zoom { animation: adZoom .22s cubic-bezier(.2,.8,.2,1) both; }
.ad-press { transition: transform .15s ease, box-shadow .2s ease, background-color .2s ease, border-color .2s ease, opacity .2s ease; }
.ad-press:hover { transform: translateY(-1px); }
.ad-press:active { transform: scale(.97); }
.ad-card { transition: transform .2s ease, box-shadow .25s ease, border-color .2s ease; }
.ad-card:hover { transform: translateY(-3px); box-shadow: 0 14px 28px -14px rgba(15,23,42,.35); }
.ad-card .ad-overlay { opacity: 0; transition: opacity .2s ease; }
.ad-card:hover .ad-overlay, .ad-card:focus-within .ad-overlay { opacity: 1; }
.ad-slide { transition: transform .6s cubic-bezier(.4,0,.2,1); }
@media (prefers-reduced-motion: reduce) {
  .ad-in, .ad-fade, .ad-zoom { animation: none; }
  .ad-press:hover, .ad-press:active, .ad-card:hover { transform: none; }
  .ad-slide { transition: none; }
}
`;

const CARD =
  "bg-white dark:bg-secondary-dark-bg rounded-xl border border-slate-200 dark:border-slate-700";

const MAX_SIZE = 5000000; // 5 MB, same limit as before

/* ================= helpers ================= */

const parseDate = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDate = (d) =>
  d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";
const fmtTime = (d) =>
  d ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "";
const fmtSize = (b) =>
  b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;

const readDims = (url) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = url;
  });

let uid = 0;

/* ================= page ================= */

const AdsImages = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dims, setDims] = useState({}); // ads_id -> {w,h}
  const [queue, setQueue] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const [view, setView] = useState("grid"); // grid | list
  const [sortBy, setSortBy] = useState("newest");
  const [selectedIds, setSelectedIds] = useState([]);

  const [lightbox, setLightbox] = useState(null); // index in sorted list
  const [confirmDelete, setConfirmDelete] = useState(null); // array of ads_id
  const [deleting, setDeleting] = useState(false);

  const [slide, setSlide] = useState(0);
  const [slidePaused, setSlidePaused] = useState(false);

  const toast = useRef(null);
  const fileInput = useRef(null);
  const { baseUrl } = useBaseUrl();
  const { currentMode } = useStateContext();

  /* ----- load (same API) ----- */

  const fetchPhotos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseUrl}/ads_images.php?action=list`, {
        withCredentials: true,
      });
      setPhotos(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Could not load ads" });
    }
    setLoading(false);
  }, [baseUrl]);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  useEffect(() => {
    const valid = new Set(photos.map((p) => p.ads_id));
    setSelectedIds((ids) => ids.filter((id) => valid.has(id)));
  }, [photos]);

  const onImgLoad = (id, e) => {
    const { naturalWidth: w, naturalHeight: h } = e.target;
    setDims((d) => (d[id] ? d : { ...d, [id]: { w, h } }));
  };

  /* ----- derived ----- */

  const sorted = useMemo(() => {
    const list = [...photos];
    list.sort((a, b) => {
      const da = parseDate(a.date_time)?.getTime() || Number(a.ads_id);
      const db = parseDate(b.date_time)?.getTime() || Number(b.ads_id);
      return sortBy === "oldest" ? da - db : db - da;
    });
    return list;
  }, [photos, sortBy]);

  // Most common image size among existing ads -> recommended size
  const recommended = useMemo(() => {
    const counts = {};
    Object.values(dims).forEach(({ w, h }) => {
      if (!w || !h) return;
      const key = `${w}x${h}`;
      counts[key] = (counts[key] || 0) + 1;
    });
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    if (!top) return null;
    const [w, h] = top[0].split("x").map(Number);
    return { w, h, ratio: w / h };
  }, [dims]);

  const previewRatio = recommended?.ratio || 16 / 9;

  const lastUpload = useMemo(() => {
    const newest = [...photos].sort(
      (a, b) => (parseDate(b.date_time)?.getTime() || 0) - (parseDate(a.date_time)?.getTime() || 0)
    )[0];
    return parseDate(newest?.date_time);
  }, [photos]);

  /* ----- slider preview (newest first, like the server list) ----- */

  const sliderList = photos;

  useEffect(() => {
    if (slidePaused || sliderList.length < 2) return undefined;
    const t = setInterval(() => setSlide((s) => (s + 1) % sliderList.length), 3000);
    return () => clearInterval(t);
  }, [slidePaused, sliderList.length]);

  useEffect(() => {
    if (slide >= sliderList.length) setSlide(0);
  }, [sliderList.length, slide]);

  /* ----- add files to queue ----- */

  const addFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const items = await Promise.all(
      files.map(async (file) => {
        const id = ++uid;
        const isImage = file.type.startsWith("image/");
        const url = isImage ? URL.createObjectURL(file) : null;
        const d = url ? await readDims(url) : null;
        let status = "ready";
        let error = "";
        if (!isImage) {
          status = "invalid";
          error = "Not an image file";
        } else if (file.size > MAX_SIZE) {
          status = "invalid";
          error = `Too large (${fmtSize(file.size)}). Max 5 MB`;
        }
        return { id, file, url, w: d?.w, h: d?.h, status, error, progress: 0 };
      })
    );
    setQueue((q) => [...q, ...items]);
  }, []);

  const removeFromQueue = (id) =>
    setQueue((q) => {
      const item = q.find((x) => x.id === id);
      if (item?.url) URL.revokeObjectURL(item.url);
      return q.filter((x) => x.id !== id);
    });

  const clearQueue = () =>
    setQueue((q) => {
      q.forEach((x) => x.url && URL.revokeObjectURL(x.url));
      return [];
    });

  // Paste an image with Ctrl+V anywhere on the page
  useEffect(() => {
    const onPaste = (e) => {
      const files = Array.from(e.clipboardData?.files || []).filter((f) => f.type.startsWith("image/"));
      if (files.length) addFiles(files);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [addFiles]);

  /* ----- upload (same API, one file per request) ----- */

  const uploadOne = async (item) => {
    const formData = new FormData();
    formData.append("photo", item.file);
    const res = await axios.post(`${baseUrl}/ads_images.php?action=upload`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      withCredentials: true,
      onUploadProgress: (e) => {
        if (!e.total) return;
        const pct = Math.round((e.loaded * 100) / e.total);
        setQueue((q) => q.map((x) => (x.id === item.id ? { ...x, progress: pct } : x)));
      },
    });
    if (res.data && res.data.error) throw new Error(res.data.error);
  };

  const uploadAll = async () => {
    const toUpload = queue.filter((x) => x.status === "ready" || x.status === "error");
    if (!toUpload.length) return;
    setUploading(true);
    let ok = 0;
    for (const item of toUpload) {
      setQueue((q) => q.map((x) => (x.id === item.id ? { ...x, status: "uploading", progress: 0, error: "" } : x)));
      try {
        await uploadOne(item);
        ok += 1;
        setQueue((q) => q.map((x) => (x.id === item.id ? { ...x, status: "done", progress: 100 } : x)));
      } catch (err) {
        setQueue((q) =>
          q.map((x) => (x.id === item.id ? { ...x, status: "error", error: err.message || "Upload failed" } : x))
        );
      }
    }
    setUploading(false);
    if (ok) {
      toast.current?.show({
        severity: "success",
        summary: "Uploaded",
        detail: `${ok} photo${ok > 1 ? "s" : ""} uploaded successfully`,
      });
      fetchPhotos();
      setTimeout(() => {
        setQueue((q) => {
          q.filter((x) => x.status === "done").forEach((x) => x.url && URL.revokeObjectURL(x.url));
          return q.filter((x) => x.status !== "done");
        });
      }, 1500);
    }
    if (ok < toUpload.length) {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Some uploads failed. Try again." });
    }
  };

  /* ----- delete (same API) ----- */

  const deletePhoto = async (ads_id) => {
    const res = await axios.post(
      `${baseUrl}/ads_images.php?action=delete`,
      { ads_id },
      { withCredentials: true }
    );
    if (res.data && res.data.error) throw new Error(res.data.error);
  };

  const runDelete = async () => {
    if (!confirmDelete?.length) return;
    setDeleting(true);
    let ok = 0;
    for (const id of confirmDelete) {
      try {
        await deletePhoto(id);
        ok += 1;
      } catch (e) {
        console.error(e);
      }
    }
    setDeleting(false);
    setConfirmDelete(null);
    setLightbox(null);
    setSelectedIds([]);
    if (ok) {
      toast.current?.show({
        severity: "success",
        summary: "Deleted",
        detail: `${ok} photo${ok > 1 ? "s" : ""} deleted successfully`,
      });
    }
    if (ok < confirmDelete.length) {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Delete failed for some photos" });
    }
    fetchPhotos();
  };

  /* ----- selection ----- */

  const toggleSelect = (id) =>
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  const allSelected = sorted.length > 0 && sorted.every((p) => selectedIds.includes(p.ads_id));
  const toggleAll = () => setSelectedIds(allSelected ? [] : sorted.map((p) => p.ads_id));

  /* ----- lightbox keys ----- */

  useEffect(() => {
    const onKey = (e) => {
      if (confirmDelete) {
        if (e.key === "Escape" && !deleting) setConfirmDelete(null);
        return;
      }
      if (lightbox === null) return;
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % sorted.length);
      if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + sorted.length) % sorted.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, sorted.length, confirmDelete, deleting]);

  const copyLink = (url) => {
    navigator.clipboard?.writeText(url);
    toast.current?.show({ severity: "info", summary: "Copied", detail: "Image link copied", life: 1500 });
  };

  const readyCount = queue.filter((x) => x.status === "ready" || x.status === "error").length;
  const current = lightbox !== null ? sorted[lightbox] : null;

  const sizeWarning = (w, h) => {
    if (!recommended || !w || !h) return null;
    const diff = Math.abs(w / h - recommended.ratio) / recommended.ratio;
    return diff > 0.05 ? `Shape differs from other ads (${recommended.w}×${recommended.h})` : null;
  };

  /* ================= render ================= */

  return (
    <div
      style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}}
      className="w-full p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen"
    >
      <style>{CSS}</style>
      <Toast ref={toast} />
      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = "";
        }}
      />

      {/* Header */}
      <div className="ad-in flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">App Ads</p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Slider Ads Images</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Banners shown in the app's home slider. Add, preview and remove them here.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={fetchPhotos}
            disabled={loading}
            className="ad-press inline-flex items-center gap-2 h-9 px-3.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 disabled:opacity-60"
          >
            <FaSyncAlt size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            className="ad-press inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold"
          >
            <FaPlus size={12} /> Add images
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Upload area */}
        <div className={`${CARD} ad-in p-5 xl:col-span-2`} style={{ animationDelay: "60ms" }}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => fileInput.current?.click()}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && fileInput.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              addFiles(e.dataTransfer.files);
            }}
            className={`ad-press cursor-pointer rounded-xl border-2 border-dashed p-8 text-center focus:outline-none focus:ring-2 focus:ring-cyan-500 ${
              dragOver
                ? "border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20"
                : "border-slate-300 dark:border-slate-600 hover:border-cyan-400 hover:bg-slate-50 dark:hover:bg-slate-800/50"
            }`}
          >
            <span
              className={`mx-auto w-14 h-14 rounded-full flex items-center justify-center text-2xl transition-transform ${
                dragOver ? "scale-110 bg-cyan-600 text-white" : "bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300"
              }`}
            >
              <FaCloudUploadAlt />
            </span>
            <p className="mt-3 font-semibold text-slate-900 dark:text-white">
              {dragOver ? "Drop images to add them" : "Drag images here or click to choose"}
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              JPG, PNG or WebP, up to 5 MB each. You can also paste an image with Ctrl + V.
            </p>
            {recommended && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                Other ads are {recommended.w}×{recommended.h}px. Use the same size for a clean slider.
              </p>
            )}
          </div>

          {/* Queue */}
          {queue.length > 0 && (
            <div className="ad-fade mt-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                  Ready to upload ({queue.length})
                </h3>
                <button
                  type="button"
                  onClick={clearQueue}
                  disabled={uploading}
                  className="text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 disabled:opacity-50"
                >
                  Clear all
                </button>
              </div>
              <ul className="space-y-2">
                {queue.map((item) => {
                  const warn = item.status === "ready" ? sizeWarning(item.w, item.h) : null;
                  return (
                    <li
                      key={item.id}
                      className={`ad-fade flex items-center gap-3 rounded-lg border p-2.5 ${
                        item.status === "invalid" || item.status === "error"
                          ? "border-rose-200 bg-rose-50/60 dark:border-rose-800 dark:bg-rose-900/20"
                          : item.status === "done"
                          ? "border-emerald-200 bg-emerald-50/60 dark:border-emerald-800 dark:bg-emerald-900/20"
                          : "border-slate-200 dark:border-slate-700"
                      }`}
                    >
                      <div className="w-20 h-12 shrink-0 rounded-md overflow-hidden bg-slate-100 dark:bg-slate-700">
                        {item.url && <img src={item.url} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{item.file.name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {fmtSize(item.file.size)}
                          {item.w ? `, ${item.w}×${item.h}px` : ""}
                        </p>
                        {item.status === "uploading" && (
                          <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                            <div className="h-full bg-cyan-500 transition-all" style={{ width: `${item.progress}%` }} />
                          </div>
                        )}
                        {(item.error || warn) && (
                          <p className={`text-xs mt-0.5 ${item.error ? "text-rose-600" : "text-amber-600"}`}>
                            {item.error || warn}
                          </p>
                        )}
                      </div>
                      {item.status === "done" ? (
                        <FaCheckCircle className="text-emerald-500 shrink-0" />
                      ) : item.status === "uploading" ? (
                        <span className="text-xs font-semibold text-cyan-700 tabular-nums shrink-0">{item.progress}%</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => removeFromQueue(item.id)}
                          disabled={uploading}
                          className="ad-press w-8 h-8 shrink-0 rounded-md flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-40"
                          aria-label="Remove from list"
                        >
                          <FaTimes size={12} />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={clearQueue}
                  disabled={uploading}
                  className="ad-press h-10 px-4 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={uploadAll}
                  disabled={uploading || readyCount === 0}
                  className="ad-press inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold disabled:opacity-50"
                >
                  {uploading ? <FaSyncAlt className="animate-spin" size={12} /> : <FaCloudUploadAlt />}
                  {uploading ? "Uploading..." : `Upload ${readyCount || ""} ${readyCount === 1 ? "image" : "images"}`}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Slider preview + stats */}
        <div className={`${CARD} ad-in p-5 flex flex-col`} style={{ animationDelay: "120ms" }}>
          <div className="flex items-center gap-2">
            <FaMobileAlt className="text-cyan-600" />
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Slider preview</h2>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">How the banners rotate in the app</p>

          <div
            className="mt-4 mx-auto w-full max-w-[280px] rounded-[26px] border-[6px] border-slate-800 dark:border-slate-900 bg-slate-800 p-2 shadow-lg"
            onMouseEnter={() => setSlidePaused(true)}
            onMouseLeave={() => setSlidePaused(false)}
          >
            <div className="rounded-2xl bg-white dark:bg-slate-900 p-2">
              <div className="h-2 w-16 mx-auto rounded-full bg-slate-200 dark:bg-slate-700 mb-2" />
              <div
                className="relative w-full overflow-hidden rounded-lg bg-slate-100 dark:bg-slate-800"
                style={{ aspectRatio: String(previewRatio) }}
              >
                {sliderList.length ? (
                  <div
                    className="ad-slide flex h-full"
                    style={{ transform: `translateX(-${slide * 100}%)` }}
                  >
                    {sliderList.map((p) => (
                      <img key={p.ads_id} src={p.img_path} alt="" className="w-full h-full object-cover shrink-0" />
                    ))}
                  </div>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-xs text-slate-400">
                    No ads yet
                  </div>
                )}
              </div>
              <div className="flex justify-center gap-1.5 mt-2">
                {sliderList.map((p, i) => (
                  <button
                    key={p.ads_id}
                    type="button"
                    onClick={() => setSlide(i)}
                    aria-label={`Show ad ${i + 1}`}
                    className={`h-1.5 rounded-full transition-all ${
                      i === slide ? "w-5 bg-cyan-600" : "w-1.5 bg-slate-300 dark:bg-slate-600"
                    }`}
                  />
                ))}
              </div>
              <div className="mt-3 space-y-1.5">
                <div className="h-2 rounded bg-slate-100 dark:bg-slate-800" />
                <div className="h-2 w-2/3 rounded bg-slate-100 dark:bg-slate-800" />
              </div>
            </div>
          </div>

          <dl className="mt-5 grid grid-cols-1 gap-2 text-sm">
            <div className="flex items-center justify-between rounded-lg bg-slate-50 dark:bg-slate-800/60 px-3 py-2">
              <dt className="flex items-center gap-2 text-slate-500 dark:text-slate-400"><FaImages /> Total ads</dt>
              <dd className="font-semibold text-slate-900 dark:text-white tabular-nums">{photos.length}</dd>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-slate-50 dark:bg-slate-800/60 px-3 py-2">
              <dt className="flex items-center gap-2 text-slate-500 dark:text-slate-400"><FaClock /> Last upload</dt>
              <dd className="font-semibold text-slate-900 dark:text-white">{lastUpload ? fmtDate(lastUpload) : "-"}</dd>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-slate-50 dark:bg-slate-800/60 px-3 py-2">
              <dt className="flex items-center gap-2 text-slate-500 dark:text-slate-400"><FaRulerCombined /> Banner size</dt>
              <dd className="font-semibold text-slate-900 dark:text-white tabular-nums">
                {recommended ? `${recommended.w}×${recommended.h}` : "-"}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Gallery */}
      <div className={`${CARD} ad-in mt-4`} style={{ animationDelay: "180ms" }}>
        <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3 mr-auto">
            <label className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                className="w-4 h-4 accent-cyan-600"
                aria-label="Select all"
              />
              Select all
            </label>
            <span className="text-sm text-slate-400">|</span>
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">All ads ({photos.length})</h2>
          </div>

          {selectedIds.length > 0 && (
            <button
              type="button"
              onClick={() => setConfirmDelete(selectedIds)}
              className="ad-press ad-fade inline-flex items-center gap-2 h-9 px-3.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold"
            >
              <FaTrash size={12} /> Delete {selectedIds.length} selected
            </button>
          )}

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>

          <div className="inline-flex p-1 rounded-lg bg-slate-100 dark:bg-slate-700">
            {[
              { id: "grid", icon: FaTh, label: "Grid view" },
              { id: "list", icon: FaList, label: "List view" },
            ].map((v) => {
              const Icon = v.icon;
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setView(v.id)}
                  aria-label={v.label}
                  title={v.label}
                  className={`w-8 h-7 rounded-md flex items-center justify-center transition-all active:scale-95 ${
                    view === v.id
                      ? "bg-white dark:bg-secondary-dark-bg text-slate-900 dark:text-white shadow-sm"
                      : "text-slate-500 dark:text-slate-300"
                  }`}
                >
                  <Icon size={12} />
                </button>
              );
            })}
          </div>
        </div>

        {/* Loading */}
        {loading && !photos.length && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-xl bg-slate-100 dark:bg-slate-700 animate-pulse" style={{ aspectRatio: "16/9" }} />
            ))}
          </div>
        )}

        {/* Empty */}
        {!loading && !photos.length && (
          <div className="px-5 py-14 text-center">
            <FaImages className="mx-auto text-slate-300" size={34} />
            <p className="mt-3 font-medium text-slate-700 dark:text-slate-200">No slider ads yet</p>
            <p className="text-sm text-slate-500">Add your first banner to show it in the app.</p>
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              className="ad-press mt-4 inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-cyan-600 text-white text-sm font-semibold"
            >
              <FaPlus size={12} /> Add images
            </button>
          </div>
        )}

        {/* Grid view */}
        {view === "grid" && photos.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-5">
            {sorted.map((p, i) => {
              const d = parseDate(p.date_time);
              const sel = selectedIds.includes(p.ads_id);
              const isNewest = lastUpload && d && d.getTime() === lastUpload.getTime();
              return (
                <div
                  key={p.ads_id}
                  className={`ad-card ad-in rounded-xl border overflow-hidden bg-white dark:bg-slate-800 ${
                    sel ? "border-cyan-500 ring-2 ring-cyan-500/30" : "border-slate-200 dark:border-slate-700"
                  }`}
                  style={{ animationDelay: `${Math.min(i, 10) * 40}ms` }}
                >
                  <div className="relative bg-slate-100 dark:bg-slate-700" style={{ aspectRatio: String(previewRatio) }}>
                    <img
                      src={p.img_path}
                      alt={`Ad ${p.ads_id}`}
                      onLoad={(e) => onImgLoad(p.ads_id, e)}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                    />
                    <label className="absolute top-2 left-2 w-7 h-7 rounded-md bg-white/90 dark:bg-slate-900/80 flex items-center justify-center cursor-pointer shadow-sm">
                      <input
                        type="checkbox"
                        checked={sel}
                        onChange={() => toggleSelect(p.ads_id)}
                        className="w-4 h-4 accent-cyan-600"
                        aria-label={`Select ad ${p.ads_id}`}
                      />
                    </label>
                    {isNewest && (
                      <span className="absolute top-2 right-2 text-xs font-semibold px-2 py-0.5 rounded-full bg-cyan-600 text-white shadow-sm">
                        Latest
                      </span>
                    )}
                    <div className="ad-overlay absolute inset-x-0 bottom-0 p-2 flex justify-end gap-2 bg-gradient-to-t from-slate-900/70 to-transparent">
                      <button
                        type="button"
                        onClick={() => setLightbox(i)}
                        className="ad-press inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-white/95 text-slate-800 text-sm font-medium"
                      >
                        <FaExpand size={11} /> View
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDelete([p.ads_id])}
                        className="ad-press inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-rose-600 text-white text-sm font-medium"
                      >
                        <FaTrash size={11} /> Delete
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2 px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">ID {p.ads_id}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {fmtDate(d)}, {fmtTime(d)}
                      </p>
                    </div>
                    {dims[p.ads_id] && (
                      <span className="text-xs text-slate-500 dark:text-slate-400 tabular-nums">
                        {dims[p.ads_id].w}×{dims[p.ads_id].h}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* List view (original columns) */}
        {view === "list" && photos.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="pl-5 py-3 w-8" />
                  <th className="px-4 py-3 font-medium text-left">ID</th>
                  <th className="px-4 py-3 font-medium text-left">Image</th>
                  <th className="px-4 py-3 font-medium text-left">Uploaded On</th>
                  <th className="px-4 py-3 font-medium text-left">Size</th>
                  <th className="px-4 py-3 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {sorted.map((p, i) => {
                  const d = parseDate(p.date_time);
                  return (
                    <tr key={p.ads_id} className="hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors">
                      <td className="pl-5 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(p.ads_id)}
                          onChange={() => toggleSelect(p.ads_id)}
                          className="w-4 h-4 accent-cyan-600"
                          aria-label={`Select ad ${p.ads_id}`}
                        />
                      </td>
                      <td className="px-4 py-3 tabular-nums text-slate-700 dark:text-slate-200">{p.ads_id}</td>
                      <td className="px-4 py-3">
                        <button type="button" onClick={() => setLightbox(i)} className="ad-press block">
                          <img
                            src={p.img_path}
                            alt=""
                            onLoad={(e) => onImgLoad(p.ads_id, e)}
                            className="w-32 h-[72px] object-cover rounded-md border border-slate-200 dark:border-slate-700"
                          />
                        </button>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-slate-700 dark:text-slate-200">{fmtDate(d)}</div>
                        <div className="text-xs text-slate-500">{fmtTime(d)}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-500 tabular-nums">
                        {dims[p.ads_id] ? `${dims[p.ads_id].w}×${dims[p.ads_id].h}` : "-"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => setLightbox(i)}
                            className="ad-press inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200"
                          >
                            <FaExpand size={11} /> View
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDelete([p.ads_id])}
                            className="ad-press inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-rose-50 text-rose-700 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300 text-sm font-medium"
                          >
                            <FaTrash size={11} /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ================= Lightbox ================= */}
      {current && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 1100 }}>
          <div className="ad-fade absolute inset-0 bg-black/80" onClick={() => setLightbox(null)} />
          <div className="ad-zoom relative w-full max-w-4xl">
            <div className="flex items-center justify-between text-white mb-3">
              <div>
                <p className="font-semibold">Ad ID {current.ads_id}</p>
                <p className="text-sm text-white/70">
                  {fmtDate(parseDate(current.date_time))}, {fmtTime(parseDate(current.date_time))}
                  {dims[current.ads_id] ? `  |  ${dims[current.ads_id].w}×${dims[current.ads_id].h}px` : ""}
                  {`  |  ${lightbox + 1} of ${sorted.length}`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setLightbox(null)}
                className="ad-press w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>

            <div className="relative rounded-xl overflow-hidden bg-black">
              <img src={current.img_path} alt="" className="w-full max-h-[70vh] object-contain" />
              {sorted.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => setLightbox((i) => (i - 1 + sorted.length) % sorted.length)}
                    className="ad-press absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 text-slate-800 flex items-center justify-center shadow"
                    aria-label="Previous"
                  >
                    <FaChevronLeft />
                  </button>
                  <button
                    type="button"
                    onClick={() => setLightbox((i) => (i + 1) % sorted.length)}
                    className="ad-press absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 text-slate-800 flex items-center justify-center shadow"
                    aria-label="Next"
                  >
                    <FaChevronRight />
                  </button>
                </>
              )}
            </div>

            <div className="flex flex-wrap justify-end gap-2 mt-3">
              <a
                href={current.img_path}
                target="_blank"
                rel="noreferrer"
                className="ad-press inline-flex items-center gap-2 h-9 px-3.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium"
              >
                <FaExternalLinkAlt size={11} /> Open original
              </a>
              <a
                href={current.img_path}
                download
                target="_blank"
                rel="noreferrer"
                className="ad-press inline-flex items-center gap-2 h-9 px-3.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium"
              >
                <FaDownload size={11} /> Download
              </a>
              <button
                type="button"
                onClick={() => copyLink(current.img_path)}
                className="ad-press inline-flex items-center gap-2 h-9 px-3.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium"
              >
                <FaCopy size={11} /> Copy link
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete([current.ads_id])}
                className="ad-press inline-flex items-center gap-2 h-9 px-3.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold"
              >
                <FaTrash size={11} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= Delete confirm ================= */}
      {confirmDelete && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 1200 }}>
          <div className="ad-fade absolute inset-0 bg-slate-900/50" onClick={() => !deleting && setConfirmDelete(null)} />
          <div className="ad-zoom relative w-full max-w-sm rounded-xl bg-white dark:bg-secondary-dark-bg shadow-2xl p-5">
            <span className="w-11 h-11 rounded-full bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-300 flex items-center justify-center">
              <FaExclamationCircle size={20} />
            </span>
            <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">
              Delete {confirmDelete.length > 1 ? `${confirmDelete.length} ads` : "this ad"}?
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              The image will be removed from the app slider and from the server. This cannot be undone.
            </p>
            <div className="flex gap-2 mt-3 overflow-x-auto">
              {photos
                .filter((p) => confirmDelete.includes(p.ads_id))
                .slice(0, 4)
                .map((p) => (
                  <img key={p.ads_id} src={p.img_path} alt="" className="w-20 h-12 object-cover rounded-md border border-slate-200 dark:border-slate-700" />
                ))}
            </div>
            <div className="flex gap-2 mt-5">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                disabled={deleting}
                className="ad-press flex-1 h-10 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={runDelete}
                disabled={deleting}
                className="ad-press flex-1 h-10 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {deleting ? <FaSyncAlt className="animate-spin" size={12} /> : <FaTrash size={12} />}
                {deleting ? "Deleting..." : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdsImages;